import React, { useMemo } from 'react';

import { CinemaRoomDto } from '@cineamo/api/models/cinema-room/CinemaRoomDto.types';

import { getSeatCategories } from '@cineamo/api/resources/seat-category/seat-category-api';

import { useGetAllListItems } from '@cineamo/ui/src/hooks/useGetAllListItems';

import ProgressBarAnimation from '@cineamo/ui/src/loading/ProgressBarAnimation';
import { DrawerOptions, HEX_COLOR } from '@cineamo/ui/src/seat-map/Drawer';
import { seatColors } from '@cineamo/ui/src/seat-map/exampleData';
import SeatMap from '@cineamo/ui/src/seat-map/SeatMap';

import { useSeatMap } from '../hooks/useSeatMap';
import SeatMapLegend from '../ticket-booking/SeatMapLegend';

const CinemaRoomSeatMap: React.FC<{
    cinemaRoom: CinemaRoomDto;
    seatsCountByType: {
        regularSeats: number;
        loveSeats: number;
        wheelchairSeats: number;
        dBoxSeats: number;
    };
}> = ({ cinemaRoom, seatsCountByType }) => {
    const { seats, room, isLoading } = useSeatMap(cinemaRoom?.id);

    const { data: seatCategories, isLoading: isSeatCategoriesLoading } = useGetAllListItems(
        (page) => getSeatCategories({ cinemaId: cinemaRoom.cinemaId, page }),
        'seat-categories',
        ['seat-categories', `cinemaRoomId=${cinemaRoom.id}`]
    );

    const seatColorsByCategory = useMemo<DrawerOptions['seatColorsByCategory']>(() => {
        const obj: Record<string, HEX_COLOR> = {};
        if (seatCategories) {
            seatCategories.forEach((category, index) => {
                obj[category.name ?? ''] = (category.color ?? seatColors[index % seatColors.length]) as HEX_COLOR;
            });
        }
        return obj;
    }, [seatCategories]);

    return (
        <div>
            {isLoading || isSeatCategoriesLoading || !room ? (
                <ProgressBarAnimation />
            ) : (
                <>
                    <SeatMap
                        data={{ room, seats }}
                        seatColorsByCategory={seatColorsByCategory}
                        isDisabled
                    />
                    <SeatMapLegend
                        seatCategories={seatCategories}
                        renderSeatTypes={{
                            love: seatsCountByType.loveSeats > 0,
                            wheelchair: seatsCountByType.wheelchairSeats > 0,
                            dBox: seatsCountByType.dBoxSeats > 0
                        }}
                        wrap
                    />
                </>
            )}
        </div>
    );
};

export default CinemaRoomSeatMap;
